import moment from 'moment';
import { IStore } from 'reducers/index';
import { ISettingsModel } from 'reducers/settings/models';
import { IAction } from 'components/properties-ribbon/models';
import { IFeatureToggleModel } from 'reducers/featureToggle/models';

interface IAsyncActionConstants {
	PENDING: string;
	FULFILLED: string;
	REJECTED: string;
	RESET_ERROR: string;
}

export const createAsyncActionConstants = (
	action: string,
): IAsyncActionConstants => ({
	PENDING: `${action}_PENDING`,
	FULFILLED: `${action}_FULFILLED`,
	REJECTED: `${action}_REJECTED`,
	RESET_ERROR: `${action}_RESET_ERROR`,
});
// replacing isEqual lodash
export const matchObjs = (a: any, b: any) => {
	const comparable = (o: any): any =>
		typeof o !== 'object' || !o
			? o
			: Object.keys(o)
					.sort()
					.reduce((c, key) => {
						(c as Record<string, any>)[key] = comparable(o[key]);
						return c;
					}, {} as Record<string, any>);
	return JSON.stringify(comparable(a)) === JSON.stringify(comparable(b));
};
export const parseJsonBlob = (jsonData: any) => {
	let r: any = JSON.stringify(jsonData, null, 4);
	if (jsonData && typeof jsonData === 'string') {
		try {
			r =
				jsonData !== '[]'
					? JSON.stringify(
							JSON.parse(jsonData),
							null,
							4,
					  )
					: '';
		} catch (e) {
			r = jsonData;
		}
	}
	return r;
};
export const windowOpen = (url: string, target: string = '_blank') => {
	const a = document.createElement('a');
	a.target = target;
	a.href = url;
	a.download = '';
	document.body.appendChild(a);
	a.click();
	a.remove();
};
export const timeToMs = (val: string): number => {
	const split: string[] = val.split(':');
	const hours: number = Number(split[0]) * 3600000;
	const min: number = Number(split[1]) * 60000;
	const sec: number = Number(split[2]) * 1000;
	return hours + min + sec;
};
export const convertMStoTime = (ms: any) => {
	// Get hours from milliseconds
	const hours = ms / (1000 * 60 * 60);
	const absoluteHours = Math.floor(hours);
	const h = absoluteHours > 9 ? absoluteHours : '0' + absoluteHours;

	// Get remainder from hours and convert to minutes
	const minutes = (hours - absoluteHours) * 60;
	const absoluteMinutes = Math.floor(minutes);
	const m = absoluteMinutes > 9 ? absoluteMinutes : '0' + absoluteMinutes;

	// Get remainder from minutes and convert to seconds
	const seconds = (minutes - absoluteMinutes) * 60;
	const absoluteSeconds = Math.floor(seconds);
	const s = absoluteSeconds > 9 ? absoluteSeconds : '0' + absoluteSeconds;

	return h + ':' + m + ':' + s;
};

export const formatDate = (
	date: string | Date | null | undefined,
	dateFormat: string = 'YYYY-MM-DD HH:mm:ss',
) => {
	if (date === null || date === undefined || date === '') {
		return '-';
	}
	const dateResponse = moment.utc(date).format(dateFormat);
	return dateResponse !== 'Invalid date' ? dateResponse : date.toString();
};

export const formatValueIfNullOrUndefined = (value: any) =>
	value === null || value === undefined || value === '' ? '-' : value;

export const simplifyNull = (code: string = '', name: string = '') =>
	formatValueIfNullOrUndefined(name) +
	'(' +
	formatValueIfNullOrUndefined(code) +
	')';

export const statusesValues = (statuses: any) => {
	const data = [];

	for (const item in statuses) {
		if (item) {
			data.push({
				optionText: statuses[item].text,
				value: statuses[item].value,
			});
		}
	}
	return data;
};

export const convertToMonetary = (amount: any, precession: number = 2) => {
	if (!isNaN(amount)) {
		return parseFloat(amount)
			.toFixed(precession)
			.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
	}
	return amount;
};
export const formatString = (fmt: string, ...args: any[]): string => {
	return fmt
		.split('%%')
		.reduce(
			(aggregate, chunk, i) =>
				aggregate + chunk + (args[i] || ''),
			'',
		);
};

export const includesPartialString = (str: string, args: any[]): boolean => {
	let b: boolean = false;
	args.forEach((el: any) => {
		if (str.includes(el)) {
			b = true;
		}
	});
	return b;
};
export const hasFilterValues = (filters: any): boolean =>
	!!filters &&
	Object.values(filters).some((value: any) =>
		Array.isArray(value)
			? value.some((item: any) => (typeof item === 'string' ? item.trim() !== '' : Boolean(item)))
			: typeof value === 'string' ? value.trim() !== '' : Boolean(value),
	);
// Default search window: [today - 3 months, end of today], keyed by the tab's own date filter names.
// "Today" is the user's calendar day, read as UTC because the date pickers store UTC dates; using
// moment.utc() directly would be a day off for users near midnight outside UTC.
export const lastThreeMonthsFilters = (fromKey: string, toKey: string) => () => {
	const today = moment.utc(moment().format('YYYY-MM-DD'));
	return {
		[fromKey]: today.clone().subtract(3, 'months').toISOString(),
		[toKey]: today.clone().endOf('day').toISOString(),
	};
};
export const applyFilters = (
	getState: () => IStore,
	options: { isAirInFilters?: boolean; isAirInBody?: boolean } = {},
	body: object = {},
) => ({
	PageNumber: getState().filters.pageNumber,
	Page: getState().filters.pageNumber,
	PageSize: getState().filters.pageSize,
	Sorting: getState().filters.column
		? {
				order: getState().filters.order,
				column: getState().filters.column,
		  }
		: undefined,
	Filters: {
		...getState().filters.appliedFilters,
		status: (getState().filters.appliedFilters as any).status
			? (getState().filters.appliedFilters as any).status
			: undefined,
		TransportMode: options.isAirInFilters ? 'air' : undefined,
	},
	TransportMode: options.isAirInBody ? 'air' : undefined,
	...body,
});

export const applyUsableFilters = (
	getState: () => IStore,
	body: object = {},
) => ({
	PageNumber: getState().filters.pageNumber,
	Page: getState().filters.pageNumber,
	PageSize: getState().filters.pageSize,
	Sorting: getState().filters.column
		? {
				order: getState().filters.order,
				column: getState().filters.column,
		  }
		: undefined,
	Filters: {
		...removeEmptyProperties(getState().filters.filters),
		status: (getState().filters.appliedFilters as any).status
			? (getState().filters.appliedFilters as any).status
			: undefined,
	},
	...body,
});

export const applyFiltersUpload = (
	getState: () => IStore,
	options: { isAirInFilters?: boolean; isAirInBody?: boolean } = {},
	body: any = {},
) => ({
	Paging: {
		Page:
			body && body.PageNumber
				? body.PageNumber
				: getState().filters.pageNumber,
		PageSize:
			body && body.PageSize
				? body.PageSize
				: getState().filters.pageSize,
	},
	Sorting: getState().filters.column
		? {
				order: getState().filters.order,
				column: toCapital(getState().filters.column),
		  }
		: { order: '1', column: 'UploadedDate' },
	Filters: {
		...getState().filters.filters,
		status: (getState().filters.appliedFilters as any).status
			? (getState().filters.appliedFilters as any).status
			: undefined,
		TransportMode: options.isAirInFilters ? 'air' : undefined,
	},
	TransportMode: options.isAirInBody ? 'air' : undefined,
});
export const applyPayloadExtraDocCreation = (
	getState: () => IStore,
	options: { isAirInFilters?: boolean; isAirInBody?: boolean } = {},
	body: any = {},
	param: any={}
) => ({
	...param,
	Page:
		body && body.Page
			? body.Page
			: getState().filters.pageNumber,
	PageSize:
		body && body.PageSize
			? body.PageSize
			: getState().filters.pageSize,
	Sorting: ['documentNbr', 'cblNbr', 'equipmentNbr', 'EstimatedTimeOfArrival', 'EstimatedTimeOfDeparture'].includes(getState().filters.column)
		? {
				order: getState().filters.order,
				column: toCapital(getState().filters.column),
		  }
		: {},
	TransportMode: options.isAirInBody ? 'air' : undefined,
});

export const applyFiltersDownload = (
	getState: () => IStore,
	options: { isAirInFilters?: boolean; isAirInBody?: boolean } = {},
	body: any = {},
) => ({
	Paging: {
		Page:
			body && body.PageNumber
				? body.PageNumber
				: getState().filters.pageNumber,
		PageSize:
			body && body.PageSize
				? body.PageSize
				: getState().filters.pageSize,
	},
	Sorting: getState().filters.column
		? {
				order: getState().filters.order,
				column: toCapital(getState().filters.column),
		  }
		: { order: '1', column: 'CreatedDate' },
	Filters: {
		...getState().filters.filters,
		status: (getState().filters.appliedFilters as any).status
			? (getState().filters.appliedFilters as any).status
			: undefined,
		TransportMode: options.isAirInFilters ? 'air' : undefined,
	},
	TransportMode: options.isAirInBody ? 'air' : undefined,
});

export const applyFiltersWithoutPageDetails = (
	getState: () => IStore,
	options: { isAirInFilters?: boolean; isAirInBody?: boolean } = {},
	body: object = {},
) => ({
	Sorting: getState().filters.column
		? {
				order: getState().filters.order,
				column: getState().filters.column,
		  }
		: undefined,
	Filters: {
		TransportMode: options.isAirInFilters ? 'air' : 'ocean',
		...getState().filters.appliedFilters,
		status: (getState().filters.appliedFilters as any).status
			? (getState().filters.appliedFilters as any).status
			: undefined,
	},
	TransportMode: options.isAirInBody ? 'air' : 'ocean',
	...body,
});

export const applyPageDetailsWithoutFilters = (
	getState: () => IStore,
	options: { isAirInFilters?: boolean; isAirInBody?: boolean } = {},
	body: object = {},
) => ({
	Page: getState().filters.pageNumber,
	PageNumber: getState().filters.pageNumber,
	PageSize: getState().filters.pageSize,
	...body,
});

export const convertStringToArray = (getState: () => IStore, field: string): (() => IStore) => {
	const fieldValue = getState().filters.filters[field];
	const convertedValue = !fieldValue
		? []
		: Array.isArray(fieldValue)
		? fieldValue
		: (() => {
				const normalizedValue = fieldValue.toString().trim();
				return normalizedValue
					? normalizedValue.split(/[\s,]+/).filter((item: string) => item.length > 0)
					: [];
		  })();
	return () => {
		const state = getState();
		return {
			...state,
			filters: {
				...state.filters,
				filters: {
					...state.filters.filters,
					[field]: convertedValue,
				},
			},
		} as IStore;
	};
};

/**
 * Normalizes a string filter field so that multiple values entered with or without
 * commas/spaces are trimmed and rejoined as a comma-separated string.
 * The backend field is kept as a string (not converted to an array).
 * Used by fetchETS where the API expects `ReferenceNumbers` as a plain string.
 */
export const normalizeStringFilter = (getState: () => IStore, field: string): (() => IStore) => {
	const fieldValue = getState().filters.filters[field];
	let normalizedValue: string | undefined;
	if (fieldValue && typeof fieldValue === 'string' && fieldValue.trim()) {
		normalizedValue = fieldValue
			.split(/[\s,]+/)
			.filter((item: string) => item.length > 0)
			.join(',');
	} else {
		normalizedValue = fieldValue as string | undefined;
	}
	return () => {
		const state = getState();
		return {
			...state,
			filters: {
				...state.filters,
				filters: {
					...state.filters.filters,
					[field]: normalizedValue,
				},
			},
		} as IStore;
	};
};

/**
 * Wraps getState so that filter reads resolve to the last *applied* filter
 * snapshot (filters.appliedFilters) instead of the editable, possibly
 * unapplied filter inputs (filters.filters). Used by the bulk "download all
 * results" collection, which must match the search that produced the rows
 * and total count currently displayed in the grid.
 */
export const withAppliedFilters = (getState: () => IStore): (() => IStore) => {
	return () => {
		const state = getState();
		return {
			...state,
			filters: {
				...state.filters,
				filters: { ...(state.filters.appliedFilters as any) },
			},
		} as IStore;
	};
};

/**
 * Transforms the `documentNumber` filter string in a payload into a `documentNumbers` array.
 * Splits by commas and/or whitespace, removes empty entries, and renames the field.
 * Used by all exception-management search actions (fetchExceptions, fetchLogs,
 * fetchReprocesseLogs, fetchNotifications) for both V2 BFF and V3 API paths.
 */
export const transformDocumentNumberFilter = (payload: any): any => {
	if (payload?.Filters && (payload.Filters as any).documentNumber) {
		const docNumInput = (payload.Filters as any).documentNumber;
		if (typeof docNumInput === 'string' && docNumInput.trim()) {
			(payload.Filters as any).documentNumbers = docNumInput
				.split(/[\s,]+/)
				.filter((n: string) => n.length > 0);
			delete (payload.Filters as any).documentNumber;
		}
	}
	return payload;
};

export const getHaulierText = (haulier: ISettingsModel) =>
	`${haulier.country || '-'}/${haulier.name || '-'}`;

export const hauliersToEnum = (hauliers: ISettingsModel[]) => {
	return hauliers.reduce(
		(obj, item) => ({
			...obj,
			[item.id]: getHaulierText(item),
		}),
		{},
	);
};

export const count = (array: any[], value: any) => {
	let val = 0;

	// tslint:disable-next-line:prefer-for-of
	for (let i = 0; i < array.length; i++) {
		if (array[i] === value) {
			val++;
		}
	}

	return val;
};

export const generateError = (e: any): string => {
	if (e && e.response && e.response.data) {
		const errList = JSON.parse(e.response);
		return errList.errors[0];
	}
	if (
		e &&
		e.response &&
		e.response.data &&
		e.response.data.errors &&
		e.response.data.errors.length
	) {
		return e.response.data.errors[0].value;
	}
	return 'Error has occurred';
};

export const getArrayOfButtons = (arr: boolean[], actions: any[]) =>
	arr
		.map((val: boolean, index: number) => {
			if (val) {
				return actions[index];
			}
			return null;
		})
		.filter((el) => el !== null);

export const getArrayWithMoreOptions = (arr: boolean[], actions: IAction[]) =>
	actions.map((action: IAction, index: number) => {
		if (arr[arr.length - 1] && index >= arr.length) {
			return action;
		}
		if (arr[index]) {
			return action;
		}
		return { label: '', action: () => null };
	});
export interface IError {
	key: string;
	value: string;
}

export const generateErrors = (e: any): string[] => {
	const errorMsg: string[] = [];
	if (
		e &&
		e.response &&
		e.response.data &&
		e.response.data.errors &&
		e.response.data.errors.length
	) {
		return e.response.data.errors.map((error: IError) => {
			errorMsg.push(error.value);
			return errorMsg;
		});
	}
	return errorMsg;
};

export const generateVatPartnersUpdateErrors = (vatUpdateErrors: any): string[] => {
	if (vatUpdateErrors && vatUpdateErrors.length) {
		return vatUpdateErrors.map((vatUpdateError: any, index: number) => {
			const errorMessages = vatUpdateError.errors
				.map((error: any) => `${error.columnName}: ${error.message}`)
				.join(', ');
			return `${index + 1}) rateDetailId: ${vatUpdateError.rateDetailId}, ${errorMessages}`;
		});
	}
	return ['VAT Partners Update : One or more validation errors occurred.'];
};

export const generateErrorsExtraDoc = (e: any): string[] => {
	if (e && e.response && e.response.data && e.response.data.length) {
		return e.response.data;
	}
	return ['An unexpected error occured'];
};

interface IReasonCodes {
	id: string | number;
	code: string;
	description: string;
}
export const mapReasonCodesToOptions = (reasonCodes: IReasonCodes[]): any[] =>
	reasonCodes.map((obj) => {
		if (obj.code && obj.description) {
			return {
				value: obj.code,
				optionText: `${obj.code} - ${obj.description}`,
				label: `${obj.code} - ${obj.description}`,
			};
		}
		return;
	});
export const parseQuery = (): any[] => {
	if (location.href.indexOf('?') === -1) {
		return [];
	}
	const url = location.href.substring(location.href.indexOf('?') + 1);
	const list = url.split('&');
	const arrResp: any[] = [];
	list.forEach((el: any) => {
		arrResp.push({
			key: el.split('=')[0],
			value: el.split('=')[1],
		});
	});
	return arrResp;
};
export const parseForURL = (s: string) =>
	s.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

export const parseURLParamForFilters = (s: string) =>
	s
		.split('-')
		.map((part: string) => part.charAt(0) + part.slice(1))
		.join('');

export const checkNumberValid = (value: any, type: string): boolean => {
	if (type === 'number') {
		// tslint:disable-next-line: radix
		return !isNaN(parseInt(value));
	}
	return false;
};
export const checkIsValid = (
	data: any,
	required: any[],
): {
	isValid: boolean;
	invalidList: string[];
	invalidKeys: string[];
} => {
	const lastElData =
		data && data.length > 1 ? data[data.length - 1] : data;
	let isValid: boolean = true;
	const arrInvalid: string[] = [];
	const arrInvalidKeys: string[] = [];
	if (required && lastElData) {
		required.forEach((el) => {
			if (
				el.type &&
				(!checkNumberValid(
					lastElData[el.key],
					el.type,
				) ||
					(checkNumberValid(
						lastElData[el.key],
						el.type,
					) &&
						lastElData[el.key] <= 0))
			) {
				arrInvalid.push(
					`${el.label} - must be a number`,
				);
				arrInvalidKeys.push(el.key);
				isValid = false;
			} else if (
				lastElData[el.key] === 0 ||
				lastElData[el.key] === '' ||
				(lastElData[el.key]?.length &&
					lastElData[el.key]?.length === 0)
			) {
				arrInvalid.push(el.label);
				arrInvalidKeys.push(el.key);
				isValid = false;
			} else {
				const isEmpty = String(
					lastElData[el.key],
				).replace(/\s/g, '');
				if (!isEmpty) {
					arrInvalid.push(el.label);
					arrInvalidKeys.push(el.key);
					isValid = false;
				}
			}
		});
	}
	return {
		isValid,
		invalidList: arrInvalid,
		invalidKeys: arrInvalidKeys,
	};
};

export const findObjById = (
	valueKey: any,
	baseListObj: any[],
	propertyName: any,
) => {
	const baseObj = [...baseListObj];
	return baseObj.find((element: any) => {
		if (!isNaN(valueKey)) {
			return (
				Number(valueKey) === element[propertyName] ||
				valueKey === element[propertyName]
			);
		}
		return valueKey === element[propertyName];
	});
};

export const findObjId = (
	valueKey: any,
	baseListObj: any[],
	propertyName: any,
) => {
	const baseObj = [...baseListObj];
	let returnVal = -1;
	baseObj.forEach((element: any, index: number) => {
		if (valueKey === element[propertyName]) {
			returnVal = index;
		}
	});
	return returnVal;
};
export const removeFromArray = (
	removeItem: any,
	baseObj: any[],
	valueKey?: any,
	propertyName?: any,
) => {
	const copy = baseObj;
	let index = -1;
	let constObjIndex = null;
	if (removeItem) {
		constObjIndex = findObjById(removeItem.id, baseObj, 'id');
	} else {
		constObjIndex = findObjById(valueKey, baseObj, propertyName);
	}

	index = copy.indexOf(constObjIndex);
	if (index > -1) {
		copy.splice(index, 1);
	}
	return copy;
};
export const clearFormat = (value: any, float?: number) => {
	if (!value) {
		return;
	}
	value =
		String(value).indexOf(',') > -1
			? value.replace(/,/g, '')
			: value;
	if (isNaN(value)) {
		return 0;
	}
	value = float ? parseFloat(value).toFixed(float) : parseFloat(value);
	return value;
};
export const formatData = (data: any) => {
	if (!data || (!Array.isArray(data) && data.length === 0)) {
		return;
	}
	data.forEach((el: any) => {
		const entries = el && Object.entries(el);
		for (const [k, v] of entries) {
			if (
				typeof v === 'string' &&
				v.length >= 19 &&
				moment(v).isValid() && v.indexOf('Automation') === -1
			) {
				el[k] = moment(v)
					.format('YYYY-MM-DD HH:mm:ss')
					.toString();
			} else if (
				v &&
				typeof v === 'string' &&
				moment(v).isValid() &&
				k.toLowerCase() === 'billingdate'
			) {
				el[k] = moment(v)
					.format('YYYY-MM-DD')
					.toString();
			} else if (v && typeof v === 'boolean') {
				el[k] = v.toString();
			} else if (
				k.toLowerCase() === 'weight' ||
				k.toLowerCase() === 'volume'
			) {
				el[k] = parseFloat(String(v)).toFixed(3);
			} else if (k.toLowerCase() === 'totalamount') {
				el[k] = convertToMonetary(v);
			} else {
				el[k] = formatValueIfNullOrUndefined(v);
			}
		}
	});
	return data;
};
export const toTitleCase = (str: string) => {
	if (typeof str === 'string') {
		return str.replace(/\w\S*/g, (txt: string) => {
			return (
				txt.charAt(0).toUpperCase() +
				txt.substr(1).toLowerCase()
			);
		});
	}
	return str;
};
export const checkForInvalidEntry = (
	value: any,
	invalidKey: any = 'Not Available',
) => {
	if (typeof value === 'string' && value.indexOf(invalidKey) > -1) {
		return value.replace(invalidKey, '');
	}
	return value;
};

export const fixTableData = (obj: any[]) => {
	const fc = (el: any) => {
		return Object.keys(el).forEach((value: any) => {
			return { value: el[value] };
		});
	};
	return obj.forEach((el: any, value: any) => {
		return fc(el);
	});
};
export const sortElementByKey = (
	obj: any[],
	key: string,
	direction: boolean,
	dataType: string = 'string',
): any[] => {
	const orderString = (a: any = '', b: any = '') =>
		direction ? b.localeCompare(a) : a.localeCompare(b);
	const orderNum = (a: any, b: any) => (direction ? b - a : a - b);
	const orderDate = (a: any, b: any) => {
		const c = new Date(a);
		const d = new Date(b);
		if (!direction) {
			return c.valueOf() - d.valueOf();
		} else {
			return d.valueOf() - c.valueOf();
		}
	};

	const getData = (data: any, baseObj: any) => {
		if (data && !data.props) {
			return data.toString();
		} else if (data && data.props) {
			return data.props.dangerouslySetInnerHTML.__html;
		}
		return data;
	};
	if (obj && Array.isArray(obj)) {
		return obj.sort((a: any, b: any) => {
			const aVal = getData(a[key], a)
				? getData(a[key], a)
				: '';
			const bVal = getData(b[key], b)
				? getData(b[key], b)
				: '';
			if (dataType === 'date') {
				return orderDate(aVal, bVal);
			} else if (dataType === 'number') {
				return orderNum(
					clearFormat(aVal),
					clearFormat(bVal),
				);
			} else {
				return orderString(aVal, bVal);
			}
		});
	}
	return obj;
};
export const formatNullWithValue = (value: any, defaultValue: any) =>
	value === null || value === undefined
		? defaultValue
		: checkForInvalidEntry(value);
export const formatNullValues = (value: any[], defaultValue: any) => {
	let resp: string = '';

	const addValue = (key: any) => {
		if (resp.length === 0) {
			return key;
		} else if (key === '-' && resp.endsWith('-')) {
			return resp.replace(/-$/, key);
		} else {
			return resp + key;
		}
	};

	value.forEach((key: any) => {
		if (key === null || key === undefined || key === '') {
			resp = addValue(defaultValue);
		} else {
			resp = addValue(key);
		}
	});
	return resp;
};
export const arrayToString = (key: any[]) => {
	if (!key) {
		return key;
	}
	return key.toString().replace(/,(?=[^\s])/g, ', ');
};
export const uniqueArray = (
	data: any[],
	compareBy: string,
	doubleCheck: string,
) => {
	const newArr: any[] = [];
	return data.filter((el: any, index: number) => {
		if (
			!newArr.includes(el[compareBy]) &&
			(el[doubleCheck].indexOf('-') === -1 ||
				(el[doubleCheck].indexOf('-') > -1 &&
					el[doubleCheck].length >
						el[compareBy].length + 3))
		) {
			newArr.push(el[compareBy]);
			return el;
		}
	});
};

export const isFeatureEnabled = (
	featureToFind: string,
	featureToggles: IFeatureToggleModel[],
) => {
	return (
		featureToggles.find(
			(feature: IFeatureToggleModel) =>
				feature.state &&
				feature.featureName === featureToFind,
		) !== undefined
	);
};

export const groupBydocumentType = (myArray: any[]) => {
	const newArray = myArray
		.reduce((res, currentValue) => {
			if (res.indexOf(currentValue.documentType) === -1) {
				res.push(currentValue.documentType);
			}
			return res;
		}, [])
		.map((documentType: any) => {
			return {
				documentType,
				documentStatusName: myArray
					.filter((_el) => {
						return (
							_el.documentType ===
							documentType
						);
					})
					.map((_el) => _el.documentStatusName),
			};
		});
	return newArray;
};

export const setWithExpiry = (key: string, value: string, ttl: number) => {
	const now = new Date();

	// `item` is an object which contains the original value
	// as well as the time when it's supposed to expire
	const item = {
		value,
		expiry: now.getTime() + ttl,
	};
	localStorage.setItem(key, JSON.stringify(item));
};

export const getWithExpiry = (key: string) => {
	const itemStr = localStorage.getItem(key);
	// if the item doesn't exist, return null
	if (!itemStr) {
		return null;
	}
	const item = JSON.parse(itemStr);
	const now = new Date();
	// compare the expiry time of the item with the current time
	if (now.getTime() > item.expiry) {
		// If the item is expired, delete the item from storage
		// and return null
		localStorage.removeItem(key);
		return null;
	}
	return item.value;
};

export const getAgingBucket = (hours: number) => {
	let ageTime = '';
	if (hours >= 0 && hours <= 4) {
		ageTime = '0 to 4';
	} else if (hours > 4 && hours <= 12) {
		ageTime = '4 to 12';
	} else if (hours > 12 && hours <= 24) {
		ageTime = '12 to 24';
	} else if (hours > 24 && hours <= 48) {
		ageTime = '24 to 48';
	} else {
		ageTime = '48 and above';
	}
	return ageTime;
};

export const getType = (type: string | number): string => {
	switch (type) {
		case 1:
			return 'booking';
			break;
		case 2:
			return 'equipment';
			break;
		case 3:
			return 'cbl';
			break;
		case 4:
			return 'fcr';
			break;
		case 6:
			return 'exportJob';
			break;
		case 'ets':
			return 'ets';
			break;
		case 'credit':
			return 'creditnote';
			break;
		default:
			return 'invoice';
	}
};

export const toCapital = (str: string) => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

export const checkNullValueInvoice = (el: any) => {
	if (el.hasOwnProperty('etato_empty')) {
		delete el.etato_empty;
	}
	if (el.hasOwnProperty('dateto_empty')) {
		delete el.dateto_empty;
	}
	if (el.hasOwnProperty('undefined_empty')) {
		delete el.undefined_empty;
	}
	const entries = el && Object.entries(el);
	let status = true;
	for (const [, v] of entries) {
		if (v !== undefined && v !== null && v !== '') {
			return (status = false);
		}
	}
	return status;
};

const removeEmptyProperties = (obj: object) :object => {
	const stringfiedObj = JSON.stringify(obj, (key, value) => {
	  return ['', null].includes(value) || (typeof value === 'object' &&(value.length === 0 || Object.keys(value).length === 0)) ? undefined : value;
	});
	const resObj = JSON.parse(stringfiedObj);
	return resObj;
  }

  export const concatenateStrings = (strings: Array<string | null | undefined>) => {
    let result = '';

    for (const nextString of strings) {
        // Skip null, undefined, or empty strings
        if (nextString === null || nextString === undefined || nextString.trim() === '') {
            continue;
        }

        const tempResult = result.length > 0 ? `${result},${nextString}` : nextString;

        if (tempResult.length > 254) {
            break;
        }

        result = tempResult;
    }

    return result;
}



export const applyTransportPlanLegVoyages = (data: any): any => {
    if (data?.transportPlanLegVoyages?.length > 0) {
        const legVoyage = data.transportPlanLegVoyages[0];

        const updatedVoyages = (data.voyages || []).map((voyage: any) => ({
            ...voyage,
            vesselCode: legVoyage.vesselCode,
            vesselName: legVoyage.vesselName,
            voyageNumber: legVoyage.voyageNumber,
        }));

        return {
            ...data,
            voyages: updatedVoyages,
        };
    }

    return data;
};
